import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  API_ENDPOINT,
  ONSITE_MODULE_ITEMS,
  USER_ROLES,
} from "../../utils/constants";
import { getOnsiteHeader } from "./utils";
import { createEventDays, getCalendarDateKey } from "./eventDays";

const DAY_MS = 24 * 60 * 60 * 1000;
const IST_OFFSET_MS = 330 * 60 * 1000;
const EVENT_COLORS = {
  current: "#2563eb",
  previous: "#f59e0b",
};

function getDefaultThroughDay(events, comparisonDayCount, now = new Date()) {
  const todayIST = new Date(now.getTime() + IST_OFFSET_MS)
    .toISOString()
    .slice(0, 10);
  const currentEvent = events.find(({ key }) => key === "current");

  if (!currentEvent || comparisonDayCount < 1) return 1;

  const day =
    Math.floor(
      (Date.parse(todayIST) - Date.parse(currentEvent.dates[0])) / DAY_MS
    ) + 1;

  // Before the event: Day 1.
  // During the event: through the current day.
  // After the event: through the final scheduled day.
  return Math.max(1, Math.min(comparisonDayCount, day));
}

function createComparisonEvent(key, settings) {
  const eventDays = createEventDays(
    settings?.eventStartDate,
    settings?.eventEndDate
  );

  return {
    key,
    name: settings?.eventName || settings?.id,
    year: eventDays[0]?.date.getFullYear(),
    pb: settings?.id,
    color: EVENT_COLORS[key],
    dates: eventDays.map(({ date }) => getCalendarDateKey(date)),
  };
}

const createDayKeys = (count) =>
  Array.from({ length: count }, (_, index) => `day${index + 1}`);

function comparisonLabel(day) {
  return day === 1 ? "Day 1 Comparison" : `Day 1–${day} Comparison`;
}

function eventDateLabel(event, index) {
  const date = event.dates[index];

  if (!date) {
    return `Day ${index + 1} (date not set)`;
  }

  const [year, month, day] = date.split("-");
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  return `${day}-${months[Number(month) - 1]}-${year.slice(-2)}`;
}

function registrationColumn(role, registrationTime, cutoffDate) {
  if (role === "Students") return "students";

  if (role === "Visitor" || role === "Onsite Visitor") {
    // Match DaywiseFootfall: both visitor roles use createdOn, not casing.
    return registrationTime < cutoffDate
      ? "preRegistration"
      : "spotRegistration";
  }

  // Keep all remaining eligible attendees in the combined category.
  return "special";
}

const TABLE_FIELDS = [
  "special",
  "students",
  "preRegistration",
  "spotRegistration",
  "repeats",
  "visits",
];

const shareText = (value, total) =>
  total ? `${((value / total) * 100).toFixed(1)}%` : "0%";

const signedNumber = (value) => `${value > 0 ? "+" : ""}${number(value)}`;

const differencePercent = (current, previous) =>
  previous
    ? `${current > previous ? "+" : ""}${(
        ((current - previous) / previous) *
        100
      ).toFixed(2)}%`
    : current
    ? "N/A"
    : "0.00%";

const ROLE_ORDER = [
  "Special Invitee",
  "Delegation",
  "EC",
  "Media",
  "Students",
  "Onsite Visitor",
  "Visitor",
  "Other",
];

const number = (value) => Number(value || 0).toLocaleString("en-IN");

const isScanned = (value) => value === 1 || value === true || value === "1";

function getRole(role) {
  const raw = String(role || "").trim();

  // Preserve the existing visitor / VISITOR distinction.
  if (raw === "visitor") return "Visitor";

  const key = raw.toUpperCase().replace(/[\s-]+/g, "_");

  if (["EXHIBITOR", "SERVICE"].includes(key)) {
    return null;
  }

  if (["VIP", "SPECIAL_INVITEE"].includes(key)) {
    return "Special Invitee";
  }

  if (["DELEGATION", "TRADE_DELEGATE"].includes(key)) {
    return "Delegation";
  }

  if (["STUDENT", "STUDENTS", "BULK_STUDENT"].includes(key)) {
    return "Students";
  }

  if (key === "VISITOR") return "Onsite Visitor";
  if (key === "MEDIA") return "Media";
  if (key === "EC") return "EC";

  return "Other";
}

function validTimestamp(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

// Day flags decide attendance.
// Timestamps are only needed for hourly charts.
export function aggregateEvent(records, throughDay, startDate) {
  const people = new Map();
  const selectedDays = createDayKeys(throughDay);
  // Date-only parsing matches START_DATE_EVENT / cutoffDate in DaywiseFootfall.
  const cutoffDate = new Date(startDate).getTime();

  records.forEach((record, index) => {
    const role = getRole(record.role);
    if (!role) return;

    const id =
      record.barcode_id != null && record.barcode_id !== ""
        ? `barcode:${String(record.barcode_id)}`
        : record._id != null
        ? `id:${String(record._id)}`
        : record.user_id != null && record.user_id !== ""
        ? `user:${String(record.user_id)}`
        : `row:${index}`;

    const registrationTime = new Date(record.createdOn).getTime();

    if (!people.has(id)) {
      people.set(id, {
        role,
        registrationTime,
        scans: new Map(),
      });
    }

    const person = people.get(id);

    // Duplicate records may carry missing or later registration dates.
    if (
      Number.isFinite(registrationTime) &&
      (!Number.isFinite(person.registrationTime) ||
        registrationTime < person.registrationTime)
    ) {
      person.registrationTime = registrationTime;
    }

    selectedDays.forEach((day, dayIndex) => {
      if (!isScanned(record[day])) return;

      const timestamp = validTimestamp(record[`${day}timestamp`]);
      const existing = person.scans.get(dayIndex);

      if (
        !person.scans.has(dayIndex) ||
        (timestamp !== null && (existing === null || timestamp < existing))
      ) {
        person.scans.set(dayIndex, timestamp);
      }
    });
  });

  const days = selectedDays.map((_, index) => ({
    name: `Day ${index + 1}`,
    visits: 0,
    newVisitors: 0,
    repeats: 0,
    cumulative: 0,
    missingTime: 0,
    hours: Array(24).fill(0),
    special: 0,
    students: 0,
    preRegistration: 0,
    spotRegistration: 0,
  }));

  const roles = Object.fromEntries(ROLE_ORDER.map((role) => [role, 0]));

  let unique = 0;
  let visits = 0;

  people.forEach((person) => {
    if (!person.scans.size) return;

    unique++;
    roles[person.role]++;

    const firstDay = Math.min(...person.scans.keys());

    person.scans.forEach((timestamp, dayIndex) => {
      const day = days[dayIndex];

      visits++;
      day.visits++;

      if (dayIndex === firstDay) {
        day.newVisitors++;
        const column = registrationColumn(
          person.role,
          person.registrationTime,
          cutoffDate
        );
        day[column]++;
      } else {
        // Returning attendees only appear in Repeat Visitors.
        day.repeats++;
      }

      if (timestamp === null) {
        day.missingTime++;
      } else {
        const hour = new Date(timestamp + 330 * 60 * 1000).getUTCHours();

        day.hours[hour]++;
      }
    });
  });

  let cumulative = 0;

  days.forEach((day) => {
    cumulative += day.visits;
    day.cumulative = cumulative;
  });

  const totals = Object.fromEntries(
    TABLE_FIELDS.map((field) => [
      field,
      days.reduce((sum, day) => sum + day[field], 0),
    ])
  );

  return {
    days,
    roles,
    totals,
    unique,
    visits,
    repeats: visits - unique,
  };
}

function changeText(current, previous, previousEventName) {
  if (previous === 0) {
    return current === 0
      ? "No change"
      : `N/A · ${previousEventName} is zero`;
  }

  const percent = ((current - previous) / previous) * 100;

  return `${percent > 0 ? "+" : ""}${percent.toFixed(1)}%`;
}

function ChangeChip({ current, previous, previousEventName }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={changeText(current, previous, previousEventName)}
      color={
        previous === 0 || current === previous
          ? "default"
          : current > previous
          ? "success"
          : "error"
      }
    />
  );
}

function ChartCard({ title, subtitle, children }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>
          {title}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {subtitle}
        </Typography>

        <Box
          sx={{
            height: 320,
            width: "100%",
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

function MetricCard({ title, note, metric, stats, events }) {
  const current = stats.current[metric];
  const previous = stats.previous[metric];
  const previousEvent = events.find(({ key }) => key === "previous");

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
      <CardContent>
        <Typography color="text.secondary" fontWeight={600}>
          {title}
        </Typography>

        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={2}
          my={2}
        >
          {events.map((event) => (
            <Box key={event.key}>
              <Typography
                variant="caption"
                color={event.color}
                fontWeight={700}
              >
                {event.name}
              </Typography>

              <Typography variant="h4" fontWeight={800}>
                {number(stats[event.key][metric])}
              </Typography>
            </Box>
          ))}
        </Stack>

        <ChangeChip
          current={current}
          previous={previous}
          previousEventName={previousEvent.name}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mt={1}
        >
          {note}
        </Typography>
      </CardContent>
    </Card>
  );
}

function ComparisonTable({ stats, throughDay, events }) {
  const columns = [
    {
      key: "special",
      label: "Special Invitee / EC / Trade Delegates / Media",
    },
    {
      key: "students",
      label: "Students",
    },
    {
      key: "preRegistration",
      label: "Pre-Registration",
    },
    {
      key: "spotRegistration",
      label: "Spot Registration",
    },
    {
      key: "repeats",
      label: "Repeat Visitors",
    },
    {
      key: "visits",
      label: "Visitors",
    },
  ];

  const currentEvent = events.find(({ key }) => key === "current");
  const previousEvent = events.find(({ key }) => key === "previous");
  const orderedEvents = [previousEvent, currentEvent];

  const deltaStyle = (current, previous) => ({
    color:
      current < previous
        ? "#b91c1c"
        : current > previous
        ? "#166534"
        : "text.primary",
    fontWeight: 700,
  });

  return (
    <Card variant="outlined" sx={{ mt: 3, borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>
          Day-by-day breakdown
        </Typography>

        <Typography variant="body2" color="text.secondary">
          {comparisonLabel(throughDay)} · % is each day's share of that event's
          selected total. Difference = {currentEvent.name} − {previousEvent.name};
          difference % uses {previousEvent.name} as the baseline.
        </Typography>
      </CardContent>

      <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            minWidth: 1200,
            borderCollapse: "collapse",
            "& th, & td": {
              border: "1px solid #94a3b8",
              textAlign: "center",
              px: 1.5,
              py: 1.2,
            },
            "& thead th": {
              bgcolor: "#365f8c",
              color: "white",
              fontWeight: 700,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell rowSpan={2}>Year</TableCell>

              <TableCell rowSpan={2} sx={{ minWidth: 130 }}>
                Date
              </TableCell>

              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  rowSpan={2}
                  sx={{
                    minWidth: column.key === "special" ? 170 : 95,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}

              <TableCell rowSpan={2}>%</TableCell>

              <TableCell colSpan={2}>
                Difference: {currentEvent.name} − {previousEvent.name}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Visitors</TableCell>
              <TableCell>%</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {orderedEvents.map((event) => {
              const result = stats[event.key];
              const isCurrent = event.key === "current";

              return (
                <React.Fragment key={event.key}>
                  {result.days.map((day, index) => {
                    const baseline = stats.previous.days[index].visits;

                    return (
                      <TableRow
                        key={`${event.key}-${index}`}
                        sx={{
                          bgcolor: isCurrent ? "#f8fbff" : "white",
                        }}
                      >
                        {index === 0 && (
                          <TableCell
                            component="th"
                            scope="rowgroup"
                            rowSpan={result.days.length}
                            sx={{
                              fontWeight: 700,
                              fontSize: 16,
                            }}
                          >
                            {event.year}
                          </TableCell>
                        )}

                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ whiteSpace: "nowrap" }}
                        >
                          {eventDateLabel(event, index)}
                        </TableCell>

                        {columns.map((column) => (
                          <TableCell
                            key={column.key}
                            sx={{
                              fontWeight: column.key === "visits" ? 800 : 400,
                            }}
                          >
                            {number(day[column.key])}
                          </TableCell>
                        ))}

                        <TableCell sx={{ fontWeight: 700 }}>
                          {shareText(day.visits, result.visits)}
                        </TableCell>

                        <TableCell
                          sx={isCurrent ? deltaStyle(day.visits, baseline) : {}}
                        >
                          {isCurrent
                            ? signedNumber(day.visits - baseline)
                            : "—"}
                        </TableCell>

                        <TableCell
                          sx={isCurrent ? deltaStyle(day.visits, baseline) : {}}
                        >
                          {isCurrent
                            ? differencePercent(day.visits, baseline)
                            : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  <TableRow
                    sx={{
                      bgcolor: "#fff200",
                      "& td, & th": {
                        fontWeight: 800,
                      },
                    }}
                  >
                    <TableCell component="th" scope="row" colSpan={2}>
                      Total - {event.name}
                    </TableCell>

                    {columns.map((column) => (
                      <TableCell key={column.key}>
                        {number(result.totals[column.key])}
                      </TableCell>
                    ))}

                    <TableCell>{result.visits ? "100%" : "0%"}</TableCell>

                    <TableCell
                      sx={
                        isCurrent
                          ? deltaStyle(result.visits, stats.previous.visits)
                          : {}
                      }
                    >
                      {isCurrent
                        ? signedNumber(result.visits - stats.previous.visits)
                        : "—"}
                    </TableCell>

                    <TableCell
                      sx={
                        isCurrent
                          ? deltaStyle(result.visits, stats.previous.visits)
                          : {}
                      }
                    >
                      {isCurrent
                        ? differencePercent(
                            result.visits,
                            stats.previous.visits
                          )
                        : "—"}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ p: 2 }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Category columns count first-time attendees. Later attended days count
          only under Repeat Visitors. For both visitor roles, Pre-Registration
          counts registrations before the event start date; Spot Registration
          counts registrations on or after it. Additional attendee categories are
          included with Special Invitee / EC / Trade Delegates / Media.
        </Typography>

      </Box>
    </Card>
  );
}

export function portalConversion(records, registered, throughDay, startDate) {
  const people = new Map();
  const selectedDays = createDayKeys(throughDay);
  const cutoffDate = Date.parse(startDate);

  records.forEach((record, index) => {
    // Match /preg-visitors-count exactly: uppercase VISITOR is not portal.
    if (record.role !== USER_ROLES.VISITOR) return;

    // The denominator counts user documents, so prefer the document identity.
    const id = record._id != null
      ? `id:${record._id}`
      : record.user_id != null && record.user_id !== ""
      ? `user:${record.user_id}`
      : record.barcode_id != null && record.barcode_id !== ""
      ? `barcode:${record.barcode_id}`
      : `row:${index}`;
    const registrationTime = validTimestamp(record.createdOn);
    const person = people.get(id) || { attended: false, registrationTime: null };
    person.attended ||= selectedDays.some((day) => isScanned(record[day]));
    if (registrationTime !== null &&
        (person.registrationTime === null || registrationTime < person.registrationTime)) {
      person.registrationTime = registrationTime;
    }
    people.set(id, person);
  });

  let preEventAttendees = 0;
  let eventDayAttendees = 0;
  let unknownDateAttendees = 0;
  people.forEach((person) => {
    if (!person.attended) return;
    if (person.registrationTime === null) unknownDateAttendees++;
    else if (person.registrationTime < cutoffDate) preEventAttendees++;
    else eventDayAttendees++;
  });
  const attended = preEventAttendees + eventDayAttendees + unknownDateAttendees;

  return {
    registered,
    attended,
    preEventAttendees,
    eventDayAttendees,
    unknownDateAttendees,
    difference: registered === null ? null : attended - registered,
    conversionRate: registered > 0 ? (attended / registered) * 100 : null,
    differenceRate:
      registered > 0 ? ((attended - registered) / registered) * 100 : null,
  };
}

function PortalConversionTable({ data, registrations, throughDay, events }) {
  const currentEvent = events.find(({ key }) => key === "current");
  const previousEvent = events.find(({ key }) => key === "previous");
  const rows = useMemo(
    () =>
      [previousEvent, currentEvent].map((event) => ({
        event,
        error: registrations[event.key].error,
        ...portalConversion(
          data[event.key],
          registrations[event.key].count,
          throughDay,
          event.dates[0]
        ),
      })),
    [currentEvent, data, previousEvent, registrations, throughDay]
  );
  const showUnknownDates = rows.some((row) => row.unknownDateAttendees > 0);
  const rateChange = rows.every((row) => row.conversionRate !== null)
    ? rows[1].conversionRate - rows[0].conversionRate
    : null;

  return (
    <Card variant="outlined" sx={{ mt: 3, borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>
          Portal registration conversion
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Portal visitors only (role "visitor") for {throughDay === 1 ? "Day 1" : `Days 1–${throughDay}`}.
          Registration totals come from the portal count API. Actual attendees
          counts each portal visitor once across the selected days.
        </Typography>
      </CardContent>
      <TableContainer sx={{ overflowX: "auto" }}>
        <Table
          aria-label="Portal registration conversion"
          sx={{
            minWidth: 1500,
            "& th, & td": { px: 3, py: 2 },
            "& thead th": { bgcolor: "#365f8c", color: "white", fontWeight: 700 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Event</TableCell>
              <TableCell align="right">Portal registrations</TableCell>
              <TableCell align="right">Pre-event portal registrations (attended)</TableCell>
              <TableCell align="right">Event-day portal registrations (attended)</TableCell>
              {showUnknownDates && <TableCell align="right">Registration date unknown (attended)</TableCell>}
              <TableCell align="right">Actual attendees</TableCell>
              <TableCell align="right">Conversion rate</TableCell>
              <TableCell align="right">
                Difference (attendees − registrations)
              </TableCell>
              <TableCell align="right">Difference %</TableCell>
              <TableCell>Conversion insights</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow
                key={row.event.key}
                sx={{
                  bgcolor: row.event.key === "current" ? "#f8fbff" : "white",
                  "&:last-child td, &:last-child th": { borderBottom: 0 },
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ fontWeight: 800, whiteSpace: "nowrap" }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: row.event.color,
                      mr: 1,
                    }}
                  />
                  {row.event.name}
                </TableCell>
                <TableCell align="right">
                  {row.registered === null ? "Unavailable" : number(row.registered)}
                </TableCell>
                <TableCell align="right">{number(row.preEventAttendees)}</TableCell>
                <TableCell align="right">{number(row.eventDayAttendees)}</TableCell>
                {showUnknownDates && <TableCell align="right">{number(row.unknownDateAttendees)}</TableCell>}
                <TableCell align="right" sx={{ fontWeight: 800 }}>
                  {number(row.attended)}
                </TableCell>
                <TableCell align="right">
                  <Chip
                    size="small"
                    label={row.conversionRate === null ? "N/A" : `${row.conversionRate.toFixed(2)}%`}
                    sx={{ bgcolor: "#dbeafe", color: "#1e40af", fontWeight: 800 }}
                  />
                </TableCell>
                <TableCell align="right">
                  {row.difference === null ? "—" : signedNumber(row.difference)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>
                  {row.differenceRate === null ? "N/A" : `${row.differenceRate > 0 ? "+" : ""}${row.differenceRate.toFixed(2)}%`}
                </TableCell>
                <TableCell sx={{ minWidth: 260 }}>
                  <Typography variant="body2">
                    {row.registered === null
                      ? "Registration total unavailable; conversion cannot be calculated."
                      : row.attended > row.registered
                      ? "Attendance exceeds registrations; refresh to recheck totals."
                      : `${number(row.registered - row.attended)} portal registrants have no attendance in the selected days.`}
                  </Typography>
                  {row.attended > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                      {shareText(row.eventDayAttendees, row.attended)} of attendees registered on or after the event start.
                    </Typography>
                  )}
                  {row.event.key === "current" && rateChange !== null && (
                    <Typography variant="caption" fontWeight={700} display="block" mt={0.5}>
                      Conversion {rateChange > 0 ? "+" : ""}{rateChange.toFixed(2)} percentage points vs {previousEvent.name}.
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ p: 2 }}>
        {rows.filter((row) => row.error).map((row) => (
          <Alert key={row.event.key} severity="warning" sx={{ mb: 1 }}>
            {row.event.name}: {row.error} Use Refresh to retry.
          </Alert>
        ))}
        {rows.some((row) => row.registered !== null && row.attended > row.registered) && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            Attendance exceeds the portal registration count. Refresh to recheck
            the separately loaded totals.
          </Alert>
        )}
        <Typography variant="caption" color="text.secondary">
          Conversion = actual attendees ÷ portal registrations. Difference % =
          (actual attendees − portal registrations) ÷ portal registrations.
          Rates are N/A when registrations are zero or unavailable. Portal
          registrations include those made during the event. Pre-event and
          event-day columns split attendees by registration before or on/after
          the event start date. They count attendees, not all registrations in
          those periods; the API provides only an overall registration total.
          Missing or invalid registration dates are shown separately when present.
        </Typography>
      </Box>
    </Card>
  );
}

export default function FootfallComparison() {
  const [events, setEvents] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [registrations, setRegistrations] = useState(null);

  const [throughDay, setThroughDay] = useState(1);
  const [hourlyDay, setHourlyDay] = useState(1);

  const currentEvent = events.find(({ key }) => key === "current");
  const previousEvent = events.find(({ key }) => key === "previous");
  const comparisonDayCount = events.length
    ? Math.min(...events.map(({ dates }) => dates.length))
    : 0;
  const dayKeys = useMemo(
    () => createDayKeys(comparisonDayCount),
    [comparisonDayCount]
  );

  const requestRef = useRef(null);

  const loadData = useCallback(async () => {
    requestRef.current?.abort();

    const controller = new AbortController();
    requestRef.current = controller;

    setLoading(true);
    setErrors([]);
    setData(null);
    setUpdatedAt(null);
    setRegistrations(null);
    setEvents([]);

    try {
      const config = getOnsiteHeader(ONSITE_MODULE_ITEMS.FOOTFALL_ANALYSIS);
      const baseRequestConfig = {
        ...config,
        signal: controller.signal,
        timeout: 60000,
      };
      const { data: eventSettings } = await axios.get(
        `${API_ENDPOINT}/onsite/footfall-comparison-events`,
        baseRequestConfig
      );
      const resolvedEvents = [
        createComparisonEvent("current", eventSettings?.current),
        createComparisonEvent("previous", eventSettings?.previous),
      ];

      if (
        resolvedEvents.some(
          ({ pb, name, dates }) => !pb || !name || dates.length === 0
        )
      ) {
        throw new Error("The server returned incomplete event settings.");
      }

      const sharedDayCount = Math.min(
        ...resolvedEvents.map(({ dates }) => dates.length)
      );
      const defaultDay = getDefaultThroughDay(
        resolvedEvents,
        sharedDayCount
      );

      setEvents(resolvedEvents);
      setThroughDay(defaultDay);
      setHourlyDay(defaultDay);

      const results = await Promise.allSettled(
        resolvedEvents.map(async (event) => {
          const requestConfig = {
            ...baseRequestConfig,
            params: {
              ...baseRequestConfig?.params,
              pb: event.pb,
            },
          };
          const [footfallResult, registrationResult] =
            await Promise.allSettled([
              axios.get(
                `${API_ENDPOINT}/onsite/scanned-users-footfall-small-comparison`,
                requestConfig
              ),
              axios.get(
                `${API_ENDPOINT}/onsite/preg-visitors-count`,
                requestConfig
              ),
            ]);

          if (footfallResult.status === "rejected") {
            throw footfallResult.reason;
          }

          const response = footfallResult.value;

          if (!Array.isArray(response.data)) {
            throw new Error("The server did not return a footfall array.");
          }

          const count =
            registrationResult.status === "fulfilled"
              ? registrationResult.value.data?.count
              : null;
          const validCount = Number.isSafeInteger(count) && count >= 0;

          return {
            records: response.data,
            registration: {
              count: validCount ? count : null,
              error: validCount
                ? null
                : "Portal registration count could not be loaded.",
            },
          };
        })
      );

      if (controller.signal.aborted) return;

      const failures = results.flatMap((result, index) => {
        if (result.status === "fulfilled") return [];

        const serverMessage = result.reason?.response?.data?.message;
        const message =
          typeof serverMessage === "string"
            ? serverMessage
            : result.reason?.message || "Unable to load data.";

        return [`${resolvedEvents[index].name}: ${message}`];
      });

      setErrors(failures);

      if (!failures.length) {
        setData({
          current: results[0].value.records,
          previous: results[1].value.records,
        });
        setRegistrations({
          current: results[0].value.registration,
          previous: results[1].value.registration,
        });
        setUpdatedAt(new Date());
      }
    } catch (error) {
      if (controller.signal.aborted) return;

      const serverMessage = error?.response?.data?.message;
      setErrors([
        typeof serverMessage === "string"
          ? serverMessage
          : error?.message || "Unable to load comparison events.",
      ]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();

    return () => requestRef.current?.abort();
  }, [loadData]);

  const stats = useMemo(
    () =>
      data && currentEvent && previousEvent
        ? {
            current: aggregateEvent(
              data.current,
              throughDay,
              currentEvent.dates[0]
            ),
            previous: aggregateEvent(
              data.previous,
              throughDay,
              previousEvent.dates[0]
            ),
          }
        : null,
    [currentEvent, data, previousEvent, throughDay]
  );

  const dailyData = useMemo(
    () =>
      stats
        ? stats.current.days.map((day, index) => ({
            name: day.name,
            current: day.visits,
            previous: stats.previous.days[index].visits,
            currentNew: day.newVisitors,
            previousNew: stats.previous.days[index].newVisitors,
            currentRepeats: day.repeats,
            previousRepeats: stats.previous.days[index].repeats,
          }))
        : [],
    [stats]
  );

  const cumulativeData = useMemo(
    () =>
      stats
        ? stats.current.days.map((day, index) => ({
            name: day.name,
            current: day.cumulative,
            previous: stats.previous.days[index].cumulative,
          }))
        : [],
    [stats]
  );

  const roleData = useMemo(
    () =>
      stats
        ? ROLE_ORDER.map((role) => ({
            name: role,
            current: stats.current.roles[role],
            previous: stats.previous.roles[role],
          }))
        : [],
    [stats]
  );

  const hourlyData = useMemo(
    () =>
      stats
        ? Array.from({ length: 24 }, (_, hour) => ({
            name: `${String(hour).padStart(2, "0")}:00`,
            current: stats.current.days[hourlyDay - 1].hours[hour],
            previous: stats.previous.days[hourlyDay - 1].hours[hour],
          }))
        : [],
    [stats, hourlyDay]
  );

  const missingTime = stats
    ? events.map(
        (event) =>
          `${event.name}: ${number(
            stats[event.key].days[hourlyDay - 1].missingTime
          )}`
      ).join(" · ")
    : "";

  const scope = throughDay === 1 ? "Day 1" : `Days 1–${throughDay}`;

  const tooltipFormatter = (value, name) => [number(value), name];

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 3 },
        minWidth: 0,
        bgcolor: "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ width: "100%", minWidth: 0 }}>
        <Box
          sx={{
            p: { xs: 2.5, md: 4 },
            mb: 3,
            borderRadius: 3,
            background: "linear-gradient(120deg, #0f172a, #1e3a8a)",
            color: "white",
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: "#bfdbfe",
              letterSpacing: 2,
            }}
          >
            EVENT ANALYTICS
          </Typography>

          <Typography variant="h4" fontWeight={800} mb={1}>
            Footfall comparison
          </Typography>

          <Typography sx={{ color: "#dbeafe" }}>
            {currentEvent && previousEvent
              ? `${currentEvent.name} vs ${previousEvent.name} · aligned by event day`
              : "Loading event comparison…"}
          </Typography>

          <Stack direction="row" spacing={1} mt={2}>
            {events.map((event) => (
              <Chip
                key={event.key}
                label={event.name}
                sx={{
                  bgcolor: event.color,
                  color: event.key === "previous" ? "#172033" : "white",
                  fontWeight: 700,
                }}
              />
            ))}
          </Stack>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{
            xs: "stretch",
            sm: "center",
          }}
          mb={2}
        >
          <FormControl
            size="small"
            sx={{
              minWidth: 200,
              bgcolor: "white",
            }}
          >
            <InputLabel id="comparison-through-label">
              Comparison period
            </InputLabel>

            <Select
              labelId="comparison-through-label"
              label="Comparison period"
              value={dayKeys.length ? throughDay : ""}
              disabled={!dayKeys.length}
              onChange={(event) => {
                const value = Number(event.target.value);

                setThroughDay(value);
                setHourlyDay((day) => Math.min(day, value));
              }}
            >
              {dayKeys.map((day, index) => (
                <MenuItem key={day} value={index + 1}>
                  {comparisonLabel(index + 1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {scope} in both events. Percentage changes use {previousEvent?.name ||
              "the previous event"} as the baseline.
          </Typography>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            disabled={loading}
            onClick={loadData}
            sx={{ textTransform: "none" }}
          >
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 3 }}>
          Choose the same completed event days for a fair comparison. An ongoing
          day is partial; zero means no recorded attendance, not proof that the
          day has finished.
          {currentEvent &&
            previousEvent &&
            currentEvent.dates.length !== previousEvent.dates.length && (
              <>
                {" "}This comparison is limited to their first {comparisonDayCount}
                shared event {comparisonDayCount === 1 ? "day" : "days"}.
              </>
            )}
        </Alert>

        {loading && (
          <Box mb={3}>
            <LinearProgress />
            <Typography mt={1} variant="body2">
              Loading both events…
            </Typography>
          </Box>
        )}

        {errors.map((message) => (
          <Alert severity="error" key={message} sx={{ mb: 2 }}>
            {message}
          </Alert>
        ))}

        {errors.length > 0 && (
          <Typography color="text.secondary" mb={2}>
            Both events must load before a comparison can be shown. Use Refresh
            to retry.
          </Typography>
        )}

        {stats && (
          <>
            <Grid container spacing={2} mb={3}>
              {[
                [
                  "Total footfall",
                  "visits",
                  "One attendance per person per day, including repeat days.",
                ],
                [
                  "Unique attendees",
                  "unique",
                  "Each person counted once within the selected days.",
                ],
                [
                  "Repeat-day visits",
                  "repeats",
                  "Additional attended days after a person's first attended day.",
                ],
              ].map(([title, metric, note]) => (
                <Grid item xs={12} md={4} key={metric}>
                  <MetricCard
                    title={title}
                    metric={metric}
                    note={note}
                    stats={stats}
                    events={events}
                  />
                </Grid>
              ))}
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Daily footfall"
                  subtitle="Side-by-side attendance for each event day."
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={dailyData}
                      margin={{
                        top: 15,
                        right: 15,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={tooltipFormatter} />
                      <Legend />

                      {events.map((event) => (
                        <Bar
                          key={event.key}
                          dataKey={event.key}
                          name={event.name}
                          fill={event.color}
                          radius={[5, 5, 0, 0]}
                          maxBarSize={48}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Cumulative footfall"
                  subtitle="Running total of daily attendance, including repeat days."
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={cumulativeData}
                      margin={{
                        top: 15,
                        right: 15,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={tooltipFormatter} />
                      <Legend />

                      {events.map((event) => (
                        <Line
                          key={event.key}
                          type="linear"
                          dataKey={event.key}
                          name={event.name}
                          stroke={event.color}
                          strokeWidth={3}
                          dot={{ r: 4 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <ChartCard
                  title="Attendees by category"
                  subtitle={`Unique attendees across ${scope.toLowerCase()}.`}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={roleData}
                      layout="vertical"
                      margin={{
                        top: 5,
                        right: 20,
                        left: 0,
                        bottom: 0,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />

                      <XAxis type="number" allowDecimals={false} />

                      <YAxis
                        type="category"
                        dataKey="name"
                        width={112}
                        tick={{ fontSize: 11 }}
                      />

                      <Tooltip formatter={tooltipFormatter} />
                      <Legend />

                      {events.map((event) => (
                        <Bar
                          key={event.key}
                          dataKey={event.key}
                          name={event.name}
                          fill={event.color}
                          radius={[0, 3, 3, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                      alignItems="center"
                    >
                      <Typography variant="h6" fontWeight={700}>
                        Hourly attendance
                      </Typography>

                      <FormControl size="small" sx={{ minWidth: 95 }}>
                        <InputLabel id="comparison-hour-label">Day</InputLabel>

                        <Select
                          labelId="comparison-hour-label"
                          label="Day"
                          value={dayKeys.length ? hourlyDay : ""}
                          disabled={!dayKeys.length}
                          onChange={(event) =>
                            setHourlyDay(Number(event.target.value))
                          }
                        >
                          {dayKeys.slice(0, throughDay).map((day, index) => (
                            <MenuItem key={day} value={index + 1}>
                              Day {index + 1}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>

                    <Typography variant="body2" color="text.secondary" mt={1}>
                      Recorded daily scan time · IST · all 24 hours
                    </Typography>

                    <Box
                      sx={{
                        height: 270,
                        mt: 2,
                        minWidth: 0,
                      }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={hourlyData}
                          margin={{
                            top: 10,
                            right: 15,
                            left: 0,
                            bottom: 0,
                          }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                          />

                          <XAxis
                            dataKey="name"
                            interval={3}
                            tick={{ fontSize: 11 }}
                          />

                          <YAxis allowDecimals={false} />
                          <Tooltip formatter={tooltipFormatter} />
                          <Legend />

                          {events.map((event) => (
                            <Line
                              key={event.key}
                              type="linear"
                              dataKey={event.key}
                              name={event.name}
                              stroke={event.color}
                              strokeWidth={2}
                              dot={false}
                            />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </Box>

                    <Typography variant="caption" color="text.secondary">
                      Missing/invalid scan time: {missingTime}. Included in
                      daily totals, omitted here.
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <ComparisonTable
              stats={stats}
              throughDay={throughDay}
              events={events}
            />

            {registrations && (
              <PortalConversionTable
                data={data}
                registrations={registrations}
                throughDay={throughDay}
                events={events}
              />
            )}

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              mt={2}
            >
              Exhibitor and Service roles are excluded. Unique attendees use
              barcode_id when available, then _id or user_id; people are counted
              separately in each event. Same-day re-entry scans are not available
              in this data.
            </Typography>

            {updatedAt && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={0.5}
              >
                Last refreshed:{" "}
                {updatedAt.toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                })}{" "}
                IST
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
